/**
 * Load optional third-party scripts only after the matching consent category
 * is granted. No analytics or marketing scripts are registered by default —
 * call `registerConsentScript` when a vendor is added.
 */

import {
  hasCategoryConsent,
  subscribeConsentChange,
} from './cookieConsent';

/** @type {Map<string, { id: string, category: 'analytics'|'marketing', src: string, attributes?: Record<string, string>, loaded: boolean }>} */
const registry = new Map();

let initialized = false;
let unsubscribe = null;

const canInject = () =>
  typeof document !== 'undefined' && typeof document.createElement === 'function';

const injectScript = (entry) => {
  if (!canInject() || entry.loaded) {
    return;
  }

  if (document.getElementById(entry.id)) {
    entry.loaded = true;
    return;
  }

  const script = document.createElement('script');
  script.id = entry.id;
  script.src = entry.src;
  script.async = true;

  if (entry.attributes) {
    Object.entries(entry.attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
  }

  document.head.appendChild(script);
  entry.loaded = true;
};

const removeScript = (entry) => {
  if (!canInject()) {
    return;
  }

  const node = document.getElementById(entry.id);
  if (node) {
    node.remove();
  }
  entry.loaded = false;
};

/**
 * Register a script that must wait for category consent.
 * @param {{ id: string, category: 'analytics'|'marketing', src: string, attributes?: Record<string, string> }} options
 */
export const registerConsentScript = ({ id, category, src, attributes }) => {
  if (!id || !src || (category !== 'analytics' && category !== 'marketing')) {
    throw new Error('registerConsentScript requires id, src, and category analytics|marketing');
  }

  registry.set(id, {
    id,
    category,
    src,
    attributes,
    loaded: Boolean(canInject() && document.getElementById(id)),
  });

  if (initialized) {
    syncConsentScripts();
  }
};

export const unregisterConsentScript = (id) => {
  const entry = registry.get(id);
  if (entry) {
    removeScript(entry);
    registry.delete(id);
  }
};

/** @returns {string[]} ids currently registered (for tests) */
export const listRegisteredConsentScripts = () => Array.from(registry.keys());

export const clearConsentScriptRegistry = () => {
  registry.forEach((entry) => removeScript(entry));
  registry.clear();
};

/**
 * Apply current consent: inject allowed scripts, remove revoked ones.
 * Essential portal code never goes through this registry.
 */
export const syncConsentScripts = () => {
  registry.forEach((entry) => {
    if (hasCategoryConsent(entry.category)) {
      injectScript(entry);
    } else {
      removeScript(entry);
    }
  });
};

/**
 * Whether a registered script is allowed to run under current consent.
 * Unregistered ids return false (fail closed for optional tracking).
 */
export const isConsentScriptAllowed = (id) => {
  const entry = registry.get(id);
  if (!entry) {
    return false;
  }
  return hasCategoryConsent(entry.category);
};

export const initConsentScripts = () => {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  initialized = true;
  syncConsentScripts();
  unsubscribe = subscribeConsentChange(() => {
    syncConsentScripts();
  });
};

export const teardownConsentScripts = () => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  initialized = false;
};
